import { CompleteProfileUpdateRequest, RegisterValidationFieldName } from '@api/dto';
import { TextFieldControl } from '@components/TextFieldControl';
import { DEFAULT_PAPER_WIDTH, EXAMPLE_VALID_PHONE_NUMBER } from '@constants';
import useAuth from '@contexts/AuthProvider';
import { usePaperWidthContext } from '@contexts/PaperWidthProvider';
import { useRegisterContext } from '@contexts/RegisterProvider';
import { Box, Button, Stack, Typography } from '@mui/material';
import { CountryAutocompleteControl } from 'probonio-shared-ui/component/autocomplete/CountryAutocompleteControl';
import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import useTheme from '~/theme/ThemeProvider';

import { ValidatedTextFieldControl } from '../components/ValidatedTextFieldControl';
import { useGetEmployeeQuery } from '../hooks/useGetEmployeeQuery';

const fieldMap: Record<string, string> = {
  line_1: 'address.line_1',
  postalCode: 'address.postalCode',
  city: 'address.city',
  countryCode: 'address.countryCode',
};

interface Props {
  employeeId: string;
  error: string | null;
  fieldError: string | null;
  showLoadingButton: boolean;
  onSubmit: (data: CompleteProfileUpdateRequest) => Promise<void>;
}

export default function BikeleasingForm({ employeeId, error, fieldError, showLoadingButton, onSubmit }: Props) {
  const appTheme = useTheme();
  const { t } = useTranslation();
  const { company } = useRegisterContext();
  const { setPaperWidth } = usePaperWidthContext();
  const { tokenParsed } = useAuth();
  const { data: employeeData, isSuccess: isGetEmployeeQuerySuccess } = useGetEmployeeQuery(employeeId);
  const registerValidation = employeeData?.companySettings.registerValidation;
  const employeeNumberValidation = registerValidation?.fieldValidations?.find(
    v => v.fieldName === RegisterValidationFieldName.EmployeeNumber,
  );
  const costCenterValidation = registerValidation?.fieldValidations?.find(v => v.fieldName === RegisterValidationFieldName.CostCenter);

  const { control, handleSubmit, reset, setError } = useForm<CompleteProfileUpdateRequest>({
    defaultValues: {
      firstName: '',
      lastName: '',
      workPhoneNumber: '',
      employeeNumber: '',
      costCenter: '',
      address: {
        line_1: '',
        postalCode: '',
        city: '',
        countryCode: '',
      },
    },
  });
  const address = useWatch({ control, name: 'address' });
  const isAddressRequired = !!(address && Object.values(address).some(v => v && v !== ''));

  useEffect(() => {
    setPaperWidth(600);
  }, [setPaperWidth]);

  useEffect(() => {
    if (!isGetEmployeeQuerySuccess) return;

    reset(
      {
        firstName: employeeData.firstName || tokenParsed?.given_name,
        lastName: employeeData.lastName || tokenParsed?.family_name,
        workPhoneNumber: employeeData.workPhoneNumber,
        employeeNumber: employeeData.employeeNumber,
        costCenter: employeeData.costCenter,
        address: {
          line_1: employeeData.address?.line_1,
          postalCode: employeeData.address?.postalCode,
          city: employeeData.address?.city,
          countryCode: employeeData.address?.countryCode,
        },
      },
      { keepDirtyValues: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData, isGetEmployeeQuerySuccess, reset]);

  useEffect(() => {
    if (!fieldError) return;
    setError((fieldMap[fieldError] ?? fieldError) as keyof CompleteProfileUpdateRequest, { type: 'required' }, { shouldFocus: true });
  }, [fieldError, setError]);

  const handleSave = async (formData: CompleteProfileUpdateRequest) => {
    const profileUpdate: CompleteProfileUpdateRequest = {
      ...formData,
      address: isAddressRequired ? formData.address : undefined,
    };
    await onSubmit(profileUpdate);
    setPaperWidth(DEFAULT_PAPER_WIDTH);
  };

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <Stack sx={{ gap: 3 }}>
        <Stack sx={{ gap: 1, width: '100%' }}>
          <Typography variant="h5">
            <Trans i18nKey="registration.completeData" />
          </Typography>
          <Typography>
            <Trans i18nKey="registration.employer" values={{ companyName: company?.name }} />
          </Typography>
        </Stack>
        {error && (
          <Typography color="error">
            <Trans i18nKey={error} />
          </Typography>
        )}
        <Stack sx={{ gap: 2, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              flexDirection: { xs: 'column', sm: 'row' },
            }}
            component="div"
          >
            <TextFieldControl
              required
              name="firstName"
              control={control}
              variant="outlined"
              label={t('registration.firstName')}
              autoComplete="given-name"
              rules={{ maxLength: 100 }}
            />
            <TextFieldControl
              required
              name="lastName"
              control={control}
              variant="outlined"
              label={t('registration.lastName')}
              autoComplete="family-name"
              rules={{ maxLength: 100 }}
            />
          </Box>
          <Controller
            name="workPhoneNumber"
            control={control}
            render={({ field }) => (
              <TextFieldControl
                required
                name="workPhoneNumber"
                control={control}
                variant="outlined"
                label={t('registration.phoneNumber')}
                autoComplete="tel"
                type="tel"
                rules={{
                  pattern: {
                    value: /^(?:\+[1-9]\d{0,2}|0)\d{6,14}$/,
                    message: t('registration.phoneNumberInvalid', {
                      EXAMPLE_VALID_PHONE_NUMBER: EXAMPLE_VALID_PHONE_NUMBER(),
                    }),
                  },
                }}
                onChange={e => field.onChange(e.target.value.replace(/\s+/g, ''))}
              />
            )}
          />
          <TextFieldControl
            name="address.line_1"
            control={control}
            variant="outlined"
            label={t('registration.address.street')}
            autoComplete="address-line1"
            rules={{ required: isAddressRequired, maxLength: 100, validate: value => (value ? value.trim() !== '' : true) }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <TextFieldControl
              name="address.postalCode"
              control={control}
              variant="outlined"
              label={t('registration.address.postalCode')}
              autoComplete="postal-code"
              rules={{ required: isAddressRequired, maxLength: 20, validate: value => (value ? value.trim() !== '' : true) }}
            />
            <TextFieldControl
              name="address.city"
              control={control}
              variant="outlined"
              label={t('registration.address.city')}
              autoComplete="address-level2"
              rules={{ required: isAddressRequired, maxLength: 100, validate: value => (value ? value.trim() !== '' : true) }}
            />
          </Box>
          <CountryAutocompleteControl
            enableBrowserFill
            name="address.countryCode"
            control={control}
            rules={{ required: isAddressRequired }}
            data-test-id="country-dropdown"
            textFieldProps={{
              sx: { margin: 0 },
              label: t('registration.address.country'),
              required: isAddressRequired,
            }}
            slotProps={{
              paper: {
                sx: {
                  width: 'auto',
                  [appTheme.theme.breakpoints.only('xs')]: { width: 'auto' },
                  [appTheme.theme.breakpoints.up('md')]: { maxWidth: '100%' },
                },
              },
            }}
          />
          <ValidatedTextFieldControl
            name="employeeNumber"
            control={control}
            variant="outlined"
            disabled={!!employeeData?.employeeNumber}
            registerValidation={employeeNumberValidation}
            label={t('registration.employeeRecord.employeeNumber')}
            helperText={t('registration.employeeRecord.bikeleasing.employeeNumberHint')}
            rules={{ maxLength: 100 }}
          />
          <ValidatedTextFieldControl
            name="costCenter"
            control={control}
            variant="outlined"
            disabled={!!employeeData?.costCenter}
            registerValidation={costCenterValidation}
            label={t('registration.employeeRecord.costCenter')}
            placeholder={
              costCenterValidation?.patterns?.length ? undefined : t('registration.employeeRecord.bikeleasing.costCenterPlaceholder')
            }
            rules={{ maxLength: 100 }}
          />
        </Stack>
        <Button type="submit" loading={showLoadingButton}>
          <Trans i18nKey="registration.actions.finish" />
        </Button>
      </Stack>
    </form>
  );
}
